import { View, type ViewStyle } from "react-native";
import { type LucideIcon } from "lucide-react-native";
import { colors, typography } from "../theme/tokens";
import { MoonText } from "./MoonText";
import { SectionTitle } from "./SectionTitle";

/**
 * Label + value display row. Audit task 2.1.10.
 *
 *   - the label is a subsection-level `SectionTitle` (sentence case), with
 *     its icon in front of it
 *   - regular weight value text below the label
 *
 * The label has no type style of its own: it is the app's title at its
 * subsection level, so uppercase stays reserved for page sections.
 *
 * Display-only — for tappable rows with chevron navigation use
 * MoonSettingRow instead. The two primitives have a similar look but
 * different intents:
 *   - MoonSettingRow: tappable, navigational, label-left / value-right
 *     inline layout, chevron, optional toggle trailing.
 *   - MoonDetailRow: read-only display of a field in a detail view,
 *     label-above-value stacked layout.
 *
 * State coverage (Phase 1.0 matrix):
 *   - populated / empty / coming-soon — pass an `empty` placeholder
 *     value or render a coming-soon badge via the `trailing` slot.
 *   - error / focused / disabled: N/A; display-only.
 */
type Props = {
  /** Shown in front of the label; every fact has one. */
  icon: LucideIcon;
  /** The field's name, as a subsection title. */
  label: string;
  /**
   * A short fact as a string. Anything else passes the app's text style for
   * what it is: `DialogBodyText` or `CardSubtitleText` for a paragraph,
   * `MetricValueText` for a number, `CardTitleText` for a headline value, or
   * a component such as a list of rows.
   */
  value: string | React.ReactNode;
  /** Override the value color. Defaults to `colors.textMid`. */
  valueColor?: string;
  /** Optional element rendered to the right of the value (e.g. a badge). */
  trailing?: React.ReactNode;
  testID?: string;
  style?: ViewStyle;
};

export function MoonDetailRow({
  icon,
  label,
  value,
  valueColor = colors.textMid,
  trailing,
  testID,
  style,
}: Props) {
  return (
    <View
      testID={testID}
      style={[
        {
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 10,
        },
        style,
      ]}
    >
      <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
        <SectionTitle level="subsection" icon={icon}>
          {label}
        </SectionTitle>
        {typeof value === "string" ? (
          <MoonText
            style={{
              fontFamily: typography.families.sans,
              fontSize: 14,
              color: valueColor,
              lineHeight: 20,
            }}
          >
            {value}
          </MoonText>
        ) : (
          value
        )}
      </View>
      {trailing}
    </View>
  );
}
